import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SchemaOf, object, string } from "yup";
import { DefaultErrorMessages } from "../../../../../../common/helpers/formValidationHelper";
import { LInputField } from "../../../../../../common/components/LInputField";
import { StateSelectOptions } from "../../../../../signup/components/StateSelectOptions";
import { useEffect, useState } from "react";
import { LBtn } from "../../../../../../common/components/LBtn";
import { useAppSelector } from "../../../../../../app/hooks";
import { useApi } from "../../../../../../services/useApi";
import { useModal } from "../../../../../../services/ModalContext";
import { OrgLocation, parseOrgLocationToApi } from "@store/locationsSlice";

const formSchema: SchemaOf<Omit<OrgLocation, "id">> = object({
    name: string().required(DefaultErrorMessages.required),
    addressLine1: string().required(DefaultErrorMessages.required),
    addressLine2: string().optional(),
    addressCity: string().required(DefaultErrorMessages.required),
    addressState: string().required(DefaultErrorMessages.required),
    addressZipCode: string()
        .required(DefaultErrorMessages.required)
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Postal code"))
        .length(5, DefaultErrorMessages.lengthDigits(5, "Postal code")),
    displayId: string().required(DefaultErrorMessages.required),
    email: string().required(DefaultErrorMessages.required).email("Enter a valid email"),
    phoneNumber: string()
        .required(DefaultErrorMessages.required)
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number"))
});

export const AddLocationModal = ({ nextLocationId, editedLocation, refresh }: AddLocationModalProps): JSX.Element => {
    const validateMethods = useForm<OrgLocation>({
        resolver: yupResolver(formSchema)
    });

    const isViewOrEdit = editedLocation !== undefined;
    const locationId = isViewOrEdit
        ? editedLocation.displayId?.split("-")[1] ?? "1"
        : nextLocationId?.toString() ?? "1";

    const organization = useAppSelector((x) => x.organization);

    const { addOrganizationLocation, editLocation, loading } = useApi();
    const { closeModal } = useModal();

    const formSubmitHandler: SubmitHandler<OrgLocation> = async (data: OrgLocation) => {
        const apiData = parseOrgLocationToApi(data);

        if (isViewOrEdit) {
            await editLocation(editedLocation.id, apiData);
        } else {
            await addOrganizationLocation(organization.id!, apiData);
        }

        refresh();
        closeModal();
    };

    // done because of yup validation not working on disabled
    const [displayIdDisabled, setDisplayIdDisabled] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setDisplayIdDisabled(isViewOrEdit);
        }, 100);
    }, []);

    return (
        <FormProvider {...validateMethods}>
            <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                <LInputField
                    label="Location name"
                    inputName="name"
                    placeholder="Enter location name"
                    defaultValue={editedLocation?.name}
                />
                <LInputField
                    label="Address line 1"
                    inputName="addressLine1"
                    placeholder="Address line 1"
                    defaultValue={editedLocation?.addressLine1}
                />
                <LInputField
                    label="Address line 2"
                    inputName="addressLine2"
                    placeholder="Address line 2"
                    defaultValue={editedLocation?.addressLine2}
                />
                <div className="columns">
                    <div className="column">
                        <LInputField
                            label="City"
                            inputName="addressCity"
                            placeholder="City"
                            defaultValue={editedLocation?.addressCity}
                        />
                    </div>
                    <div className="column is-3">
                        <StateSelectOptions
                            inputName="addressState"
                            hideSelectDropdownLabel
                            label="State"
                            value={editedLocation?.addressState}
                        />
                    </div>
                    <div className="column">
                        <LInputField
                            label="ZIP / Postal Code"
                            inputName="addressZipCode"
                            placeholder="e.g. 90210"
                            maxLength={5}
                            defaultValue={editedLocation?.addressZipCode}
                        />
                    </div>
                </div>
                <LInputField
                    label="Location ID"
                    inputName="displayId"
                    placeholder="Location ID"
                    tooltip="The Location ID is pre-populated but you can override this number to match your records if needed.\n
                    This number cannot be changed after initial set up."
                    defaultValue={locationId}
                    disabled={displayIdDisabled}
                />
                <div className="divider" />
                <p className="mb-4">Enter contact information (visible to examinees) below</p>
                <LInputField
                    label="Contact phone number"
                    inputName="phoneNumber"
                    placeholder="Enter phone number"
                    isNumber
                    defaultValue={editedLocation?.phoneNumber}
                    maxLength={10}
                />
                <LInputField
                    label="Contact email address"
                    inputName="email"
                    placeholder="Enter email address"
                    defaultValue={editedLocation?.email}
                />
                <div className="divider" />
                <div className="pt-2">
                    <div className="columns">
                        <div className="column" />
                        <div className="column is-4">
                            <LBtn
                                variation="outline"
                                fullWidth
                                onClick={() => {
                                    !loading && closeModal();
                                }}
                            >
                                Cancel
                            </LBtn>
                        </div>
                        <div className="column is-1" />
                        <div className="column is-4">
                            <LBtn fullWidth type="submit" loading={loading}>
                                Continue
                            </LBtn>
                        </div>
                        <div className="column" />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

interface AddLocationModalProps {
    nextLocationId?: number;
    editedLocation?: OrgLocation;
    refresh: () => void;
}
